// Shared types, computed status buckets, and the SEO landing-page lead
// threshold for the public county-stats surface (/public, /public/:countyId,
// /foreclosures/:countyId). Single source of truth so PublicDashboard.tsx and
// CountyLandingPage.tsx can't drift on the bucket definitions or the tiering
// threshold. Extracted from PublicDashboard.tsx (#965) — behavior unchanged.

import { statusBucket, STATUS_META } from "./statusMeta";
import { LEAD_SIGNAL_PRIORITY, leadTypeMeta } from "./leadTypeMeta";

export interface ZipCount {
  zip_code: string;
  count: number;
}

export interface CountyStats {
  county_id: string;
  county_name: string;
  state: string;
  total_active: number;
  status_breakdown: Record<string, number>;
  // total_active split by each property's PRIMARY lead type (the same
  // highest-priority-signal rule as the app's headline badge), so the values sum
  // to total_active. Optional: a cached response from before the field existed
  // simply renders without the breakdown. See PublicCountyStats in
  // backend/internal/handlers/public_handler.go for why this must sit beside
  // total_active: status says where a property is in the process, lead type
  // says why it is a lead, and most "active" rows are not foreclosures.
  lead_type_breakdown?: Record<string, number>;
  top_zip_codes: ZipCount[];
  // Freshness of the underlying data (MAX(properties.updated_at) for the
  // county) — omitted when the county has no properties yet (#675).
  updated_at?: string;
}

export interface AllStatsResponse {
  counties: CountyStats[];
  generated_at: string;
}

// Collapse the status engine into the 3 buckets the public views show —
// auction (live scheduled-sale / upset-bid lifecycle), pre (lead: filed,
// no sale yet), resolved. The bucket assignment lives on STATUS_META
// (`lib/statusMeta.ts`), the same source the map pins and legend read, so
// the public views and the in-app map can no longer drift (#1626 — this
// removed one of the four hand-copied status lists; the pinned definitions
// are in docs/RELAUNCH.md, and the backend mirror is publicActiveStatuses
// in backend/internal/handlers/public_handler.go).
// Unknown statuses land in the resolved/sold bucket, matching statusMeta's
// muted fallback (a value we can't classify must not inflate live counts).
export function bucketStatuses(sb: Record<string, number> | null | undefined): {
  auction: number;
  pre: number;
  sold: number;
} {
  let auction = 0;
  let pre = 0;
  let sold = 0;
  for (const [k, v] of Object.entries(sb || {})) {
    const n = Number(v) || 0;
    const bucket = statusBucket(k);
    if (bucket === "auction") auction += n;
    else if (bucket === "lead") pre += n;
    else sold += n; // resolved: sold, redeemed, withdrawn, auction_passed, delisted + unknowns
  }
  return { auction, pre, sold };
}

export const pct = (part: number, total: number): string =>
  total > 0 ? `${(part / total) * 100}%` : "0%";

// ─── Lead-type breakdown ───────────────────────────────────────────────────
//
// total_active counts every property on the active list, and most of them are
// not foreclosures: on 2026-10-06 about 86% of the statewide total (and 97% of
// Davidson County's 4,072) were tax-delinquent properties that have no
// foreclosure filed. The public pages used to call all of it "foreclosure
// leads" and "pre-foreclosure". These helpers turn the API's per-county
// lead_type_breakdown into the rows and sentences that say what the numbers are.

export const OTHER_LEAD_TYPE = "other";

export interface LeadTypeRow {
  key: string;
  label: string;
  color: string;
  /** One plain sentence on what this lead type means. */
  blurb: string;
  count: number;
}

// Worded to claim only what the data shows. "No foreclosure has been filed"
// for tax_delinquent holds because a property with a filing would carry a
// higher-priority signal and be counted under that type instead.
const LEAD_TYPE_BLURBS: Record<string, string> = {
  tax_delinquent: "Behind on property taxes. No foreclosure filing is on record.",
  mortgage_foreclosure: "A lender has started foreclosure on the mortgage.",
  tax_foreclosure: "The county has started foreclosure over unpaid property taxes.",
  hoa_foreclosure: "An HOA has started foreclosure over unpaid dues.",
  foreclosure: "A foreclosure case is on file.",
  execution_sale: "A sheriff's sale to satisfy a court judgment.",
  trustee_substitution: "The lender appointed a new trustee on the loan. An early warning: no foreclosure has been filed.",
  code_enforcement: "An open code-enforcement case on the property.",
  condemnation: "The city has ordered the building repaired, closed or demolished.",
  probate: "The owner's estate is in probate.",
  divorce: "A divorce filing involves the owner.",
  [OTHER_LEAD_TYPE]: "On the active list without a lead type attached yet.",
};

const leadTypeRank = (key: string): number => {
  const i = LEAD_SIGNAL_PRIORITY.indexOf(key as (typeof LEAD_SIGNAL_PRIORITY)[number]);
  return i === -1 ? LEAD_SIGNAL_PRIORITY.length : i;
};

/**
 * The breakdown as display rows: largest first, ties broken by the app's lead
 * priority, zero and malformed counts dropped. Labels and colors come from
 * leadTypeMeta so the public pages and the app say the same thing.
 */
export function leadTypeRows(breakdown: Record<string, number> | null | undefined): LeadTypeRow[] {
  const rows: LeadTypeRow[] = [];
  for (const [key, raw] of Object.entries(breakdown ?? {})) {
    const count = Number(raw) || 0;
    if (count <= 0) continue;
    const isOther = key === OTHER_LEAD_TYPE;
    const meta = leadTypeMeta(key);
    rows.push({
      key,
      label: isOther ? "Other" : meta.label,
      color: isOther ? STATUS_META.delisted.color : meta.color,
      blurb: LEAD_TYPE_BLURBS[key] ?? "An active lead of another type.",
      count,
    });
  }
  return rows.sort(
    (a, b) => b.count - a.count || leadTypeRank(a.key) - leadTypeRank(b.key) || a.key.localeCompare(b.key),
  );
}

export interface LeadMix {
  /** A foreclosure of any kind is on file: mortgage, tax, HOA, generic, sheriff's sale. */
  foreclosure: number;
  taxDelinquent: number;
  /** Code enforcement or condemnation. */
  codeViolation: number;
  other: number;
}

const FORECLOSURE_TYPES = new Set([
  "tax_foreclosure",
  "mortgage_foreclosure",
  "foreclosure",
  "hoa_foreclosure",
  "execution_sale",
]);
const CODE_VIOLATION_TYPES = new Set(["code_enforcement", "condemnation"]);

/** Collapses the breakdown into the four groups a reader cares about. */
export function leadMix(breakdown: Record<string, number> | null | undefined): LeadMix {
  const mix: LeadMix = { foreclosure: 0, taxDelinquent: 0, codeViolation: 0, other: 0 };
  for (const [key, raw] of Object.entries(breakdown ?? {})) {
    const n = Number(raw) || 0;
    if (n <= 0) continue;
    if (FORECLOSURE_TYPES.has(key)) mix.foreclosure += n;
    else if (key === "tax_delinquent") mix.taxDelinquent += n;
    else if (CODE_VIOLATION_TYPES.has(key)) mix.codeViolation += n;
    else mix.other += n;
  }
  return mix;
}

/**
 * One sentence fragment for the page copy, e.g. "By lead type: 159 with a
 * foreclosure filing, 3,991 tax-delinquent (...)". Empty groups are left out.
 * Null when there is nothing to say (an old cached response, or no leads).
 */
export function leadMixSummary(mix: LeadMix): string | null {
  const fmt = (n: number) => n.toLocaleString();
  const parts: string[] = [];
  if (mix.foreclosure > 0) parts.push(`${fmt(mix.foreclosure)} with a foreclosure filing`);
  if (mix.taxDelinquent > 0) {
    parts.push(`${fmt(mix.taxDelinquent)} tax-delinquent (behind on property taxes, no foreclosure filing on record)`);
  }
  if (mix.codeViolation > 0) parts.push(`${fmt(mix.codeViolation)} with an open code-enforcement case`);
  if (mix.other > 0) parts.push(`${fmt(mix.other)} other`);
  return parts.length > 0 ? `By lead type: ${parts.join(", ")}` : null;
}

// ─── Programmatic SEO landing-page tiering (issue #965) ────────────────────
//
// Building a real content page for all ~100 active NC counties is a
// doorway-page risk: most counties have too few active leads to read as a
// real page, which search engines treat as thin/duplicate content. Only
// counties whose live `total_active` clears this bar get a dedicated
// /foreclosures/:countyId landing page linked from /public and the sitemap.
// `total_active` is the SAME "active leads" figure /public already shows —
// it comes straight from GET /api/v1/public/county-stats(/:id), which sums
// publicActiveStatuses (auction_scheduled, pending_confirmation, postponed,
// notice_of_default, notice_filed, in_rem, res_plus) in
// backend/internal/handlers/public_handler.go. No new backend endpoint or
// definition of "active" was introduced for this feature.
//
// ⚠️ FOUNDER-ADJUSTABLE DEFAULT: 20 was chosen by the implementing session
// as a reasonable starting bar (a live pull on 2026-08-03 put 13 of 100
// active counties at or above it), not a data-backed optimum. Change this
// single constant if 20 is too high/low — the qualifying-county set is
// always computed live from the API, never hardcoded, so nothing else in
// the app needs to change. `frontend/public/sitemap.xml`'s hand-maintained
// URL list is the one exception (see its own header comment) — a threshold
// change means re-running the query and updating that file's entries too.
export const COUNTY_LANDING_LEAD_THRESHOLD = 20;

export function qualifiesForLandingPage(totalActive: number): boolean {
  return totalActive >= COUNTY_LANDING_LEAD_THRESHOLD;
}

// Counties that already have a hand-authored marketing guide (real prose,
// not data-templated) at a legacy flat URL — see CountyTaxForeclosureGuide.tsx
// + MecklenburgTaxForeclosures.tsx. The programmatic /foreclosures/:countyId
// page must never compete with these for the same "[county] foreclosures"
// search intent:
//   - PublicDashboard's county-card grid links these counties to /public/:id
//     (the live-data dashboard) exactly as it did before this feature,
//     never to /foreclosures/:countyId.
//   - CountyLandingPage itself redirects to the hand-authored guide if
//     someone reaches /foreclosures/:countyId directly for one of these.
// Keyed by county_id (the same slug used in /public/:countyId) -> the
// existing guide's route.
export const HAND_AUTHORED_GUIDE_URLS: Record<string, string> = {
  "mecklenburg-nc": "/mecklenburg-tax-foreclosures",
  "cabarrus-nc": "/cabarrus-tax-foreclosures",
  "gaston-nc": "/gaston-tax-foreclosures",
  "lincoln-nc": "/lincoln-tax-foreclosures",
};
